/**
 * Bilingual translations: Thai (default) and English.
 * Add new keys here, then use `const t = useT(); t('nav.dashboard')` anywhere.
 */
export type Lang = 'th' | 'en';

const dict = {
  // ─── System ───
  'system.title':       { th: 'ระบบธนาคารข้อสอบ', en: 'Question Bank System' },
  'system.school':      { th: 'โรงเรียนใบบุญลำพูน', en: 'Baiboon Lamphun School' },
  'system.loading':     { th: 'กำลังโหลด...', en: 'Loading...' },

  // ─── Login ───
  'login.title':        { th: 'เข้าสู่ระบบ', en: 'Sign in' },
  'login.welcome':      { th: 'ยินดีต้อนรับกลับ เข้าสู่ระบบเพื่อดำเนินการต่อ', en: 'Welcome back. Sign in to continue.' },
  'login.username':     { th: 'ชื่อผู้ใช้', en: 'Username' },
  'login.password':     { th: 'รหัสผ่าน', en: 'Password' },
  'login.submit':       { th: 'เข้าสู่ระบบ', en: 'Sign in' },
  'login.username_placeholder': { th: 'กรอกชื่อผู้ใช้...', en: 'Enter your username...' },
  'login.password_placeholder': { th: 'กรอกรหัสผ่าน...', en: 'Enter your password...' },
  'login.tagline':      { th: 'สร้าง จัดการ และแบ่งปันข้อสอบคุณภาพสูงสำหรับการศึกษา ทำงานร่วมกันได้ง่าย ขั้นตอนการอนุมัติชัดเจน', en: 'Create, manage and share high-quality exam questions. Built for collaboration, with a clear approval flow.' },
  'login.success':      { th: 'เข้าสู่ระบบสำเร็จ', en: 'Signed in successfully' },
  'login.failed':       { th: 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง', en: 'Incorrect username or password' },

  // ─── Navigation ───
  'nav.dashboard':            { th: 'หน้าหลัก', en: 'Dashboard' },
  'nav.my_work':              { th: 'งานของฉัน', en: 'My work' },
  'nav.my_questions':         { th: 'ข้อสอบของฉัน', en: 'My questions' },
  'nav.approvals':            { th: 'อนุมัติข้อสอบ', en: 'Approvals' },
  'nav.question_bank_section':{ th: 'คลังข้อสอบ', en: 'Question library' },
  'nav.question_bank':        { th: 'ธนาคารข้อสอบ', en: 'Question bank' },
  'nav.learning_indicators':  { th: 'จัดการตัวชี้วัด', en: 'Learning indicators' },
  'nav.reports':              { th: 'รายงานระบบ', en: 'Reports' },
  'nav.system_admin':         { th: 'จัดการระบบ', en: 'Administration' },
  'nav.users':                { th: 'จัดการผู้ใช้', en: 'Users' },
  'nav.subject_permissions':  { th: 'จัดการสิทธิ์กลุ่มสาระ', en: 'Subject permissions' },
  'nav.metadata':             { th: 'ข้อมูลพื้นฐาน', en: 'Metadata' },
  'nav.activity':             { th: 'กิจกรรม', en: 'Activity log' },
  'nav.backup':               { th: 'จัดการ Backup', en: 'Backups' },

  // ─── Common buttons ───
  'btn.create':         { th: 'สร้าง', en: 'Create' },
  'btn.edit':           { th: 'แก้ไข', en: 'Edit' },
  'btn.delete':         { th: 'ลบ', en: 'Delete' },
  'btn.save':           { th: 'บันทึก', en: 'Save' },
  'btn.cancel':         { th: 'ยกเลิก', en: 'Cancel' },
  'btn.confirm':        { th: 'ยืนยัน', en: 'Confirm' },
  'btn.search':         { th: 'ค้นหา', en: 'Search' },
  'btn.close':          { th: 'ปิด', en: 'Close' },
  'btn.back':           { th: 'กลับ', en: 'Back' },
  'btn.next':           { th: 'ถัดไป', en: 'Next' },
  'btn.previous':       { th: 'ก่อนหน้า', en: 'Previous' },
  'btn.add':            { th: 'เพิ่ม', en: 'Add' },
  'btn.remove':         { th: 'ลบ', en: 'Remove' },
  'btn.view':           { th: 'ดู', en: 'View' },
  'btn.download':       { th: 'ดาวน์โหลด', en: 'Download' },
  'btn.upload':         { th: 'อัปโหลด', en: 'Upload' },
  'btn.export':         { th: 'ส่งออก', en: 'Export' },
  'btn.import':         { th: 'นำเข้า', en: 'Import' },
  'btn.approve':        { th: 'อนุมัติ', en: 'Approve' },
  'btn.reject':         { th: 'ปฏิเสธ', en: 'Reject' },
  'btn.submit':         { th: 'ส่ง', en: 'Submit' },
  'btn.refresh':        { th: 'รีเฟรช', en: 'Refresh' },

  // ─── Status badges ───
  'status.draft':       { th: 'ร่าง', en: 'Draft' },
  'status.pending':     { th: 'รออนุมัติ', en: 'Pending' },
  'status.approved':    { th: 'อนุมัติแล้ว', en: 'Approved' },
  'status.rejected':    { th: 'ไม่อนุมัติ', en: 'Rejected' },

  // ─── Roles ───
  'role.admin':         { th: 'ผู้ดูแลระบบ', en: 'Administrator' },
  'role.academic_admin':{ th: 'หัวหน้าวิชาการ', en: 'Academic admin' },
  'role.teacher':       { th: 'ครูผู้สอน', en: 'Teacher' },
  'role.executive':     { th: 'ผู้บริหาร', en: 'Executive' },

  // ─── Greetings ───
  'greet.morning':      { th: 'อรุณสวัสดิ์', en: 'Good morning' },
  'greet.afternoon':    { th: 'สวัสดีตอนบ่าย', en: 'Good afternoon' },
  'greet.evening':      { th: 'สวัสดีตอนเย็น', en: 'Good evening' },

  // ─── Top bar / user menu ───
  'topbar.search_placeholder': { th: 'ค้นหาข้อสอบ, เมนู...', en: 'Search questions, menu...' },
  'topbar.open_menu':       { th: 'เปิดเมนู', en: 'Open menu' },
  'topbar.notifications':   { th: 'แจ้งเตือน', en: 'Notifications' },
  'topbar.lang_th':         { th: '🇹🇭 ไทย', en: '🇹🇭 ไทย' },
  'sidebar.close_menu':     { th: 'ปิดเมนู', en: 'Close menu' },
  'sidebar.footer_hint':    { th: 'กด', en: 'Press' },
  'sidebar.footer_search':  { th: 'เพื่อค้นหา', en: 'to search' },

  // ─── Stub page ───
  'stub.in_progress':       { th: 'หน้านี้กำลังพัฒนา', en: 'This page is in development' },
  'stub.desc':              { th: 'กำลังย้ายจากเว็บเดิม (v7) มาใช้ design system ใหม่ ระหว่างนี้ฟังก์ชั่นเดิมยังใช้ได้ที่', en: 'Migrating from the old site (v7) to the new design system. In the meantime the original feature is available at' },
  'usermenu.theme':        { th: 'ธีม', en: 'Theme' },
  'usermenu.theme_light':  { th: 'สว่าง', en: 'Light' },
  'usermenu.theme_dark':   { th: 'มืด', en: 'Dark' },
  'usermenu.theme_system': { th: 'ตามระบบ', en: 'System' },
  'usermenu.language':     { th: 'ภาษา', en: 'Language' },
  'usermenu.change_password': { th: 'เปลี่ยนรหัสผ่าน', en: 'Change password' },
  'usermenu.logout':       { th: 'ออกจากระบบ', en: 'Sign out' },
  'usermenu.notifications':{ th: 'แจ้งเตือน', en: 'Notifications' },

  // ─── Dashboard ───
  'dash.welcome':           { th: '👋', en: '👋' },
  'dash.subtitle.admin':    { th: 'ภาพรวมระบบธนาคารข้อสอบวันนี้', en: 'Question bank overview' },
  'dash.subtitle.academic': { th: 'ตรวจสอบและอนุมัติข้อสอบเพื่อรักษาคุณภาพ', en: 'Review and approve questions to maintain quality' },
  'dash.subtitle.teacher':  { th: 'แบ่งปันความรู้ผ่านข้อสอบคุณภาพสูง', en: 'Share knowledge through high-quality questions' },
  'dash.subtitle.executive':{ th: 'ดูภาพรวมและรายงานของระบบ', en: 'View system overview and reports' },
  'dash.stat.total':        { th: 'ข้อสอบทั้งหมด', en: 'Total questions' },
  'dash.stat.pending':      { th: 'รออนุมัติ', en: 'Pending approval' },
  'dash.stat.approved':     { th: 'อนุมัติแล้ว', en: 'Approved' },
  'dash.stat.users':        { th: 'ผู้ใช้ในระบบ', en: 'Users' },
  'dash.stat.my_questions': { th: 'ข้อสอบของฉัน', en: 'My questions' },
  'dash.recent_questions':  { th: 'ข้อสอบล่าสุด', en: 'Recent questions' },
  'dash.view_all':          { th: 'ดูทั้งหมด', en: 'View all' },
  'dash.create_new':        { th: 'สร้างข้อสอบใหม่', en: 'Create new question' },
  'dash.empty_title':       { th: 'ยังไม่มีข้อสอบ', en: 'No questions yet' },
  'dash.empty_desc':        { th: 'เริ่มสร้างข้อสอบแรกของคุณเพื่อเก็บไว้ในธนาคารข้อสอบ', en: 'Create your first question to add to the bank' },

  // ─── Questions list ───
  'q.title':                { th: 'ข้อสอบของฉัน', en: 'My questions' },
  'q.subtitle':             { th: 'จัดการข้อสอบที่คุณสร้าง', en: 'Manage the questions you created' },
  'q.search':               { th: 'ค้นหาข้อสอบ...', en: 'Search questions...' },
  'q.col.question':         { th: 'คำถาม', en: 'Question' },
  'q.col.subject':          { th: 'วิชา', en: 'Subject' },
  'q.col.grade':            { th: 'ระดับชั้น', en: 'Grade' },
  'q.col.status':           { th: 'สถานะ', en: 'Status' },
  'q.col.created':          { th: 'วันที่สร้าง', en: 'Created' },
  'q.col.creator':          { th: 'ผู้สร้าง', en: 'Creator' },
  'q.col.type':             { th: 'ประเภท', en: 'Type' },
  'q.col.topic':            { th: 'หัวข้อ', en: 'Topic' },
  'q.col.actions':          { th: 'การดำเนินการ', en: 'Actions' },
  'q.empty':                { th: 'ยังไม่มีข้อสอบ', en: 'No questions yet' },
  'q.import_csv':           { th: 'นำเข้า CSV', en: 'Import CSV' },
  'q.delete_confirm':       { th: 'ลบข้อสอบนี้?', en: 'Delete this question?' },
  'q.delete_irreversible':  { th: 'การลบนี้ไม่สามารถย้อนกลับได้', en: 'This action cannot be undone' },

  // ─── Filters ───
  'filter.all_subjects':    { th: 'กลุ่มสาระทั้งหมด', en: 'All subject groups' },
  'filter.all_grades':      { th: 'ระดับชั้นทั้งหมด', en: 'All grades' },
  'filter.all_difficulties':{ th: 'ความยากทุกระดับ', en: 'All difficulties' },
  'filter.all_status':      { th: 'ทุกสถานะ', en: 'All statuses' },
  'filter.all_roles':       { th: 'ทุกบทบาท', en: 'All roles' },

  // ─── Empty / errors ───
  'empty.no_results':       { th: 'ไม่พบผลการค้นหา', en: 'No results found' },
  'empty.no_data':          { th: 'ยังไม่มีข้อมูล', en: 'No data yet' },
  'empty.try_clear_filters':{ th: 'ลองล้างตัวกรอง หรือพิมพ์คำค้นหาอื่น', en: 'Try clearing filters or different keywords' },
  'error.load_failed':      { th: 'โหลดข้อมูลไม่สำเร็จ ลองรีเฟรชหน้านี้อีกครั้ง', en: 'Failed to load — try refreshing the page' },
  'error.save_failed':      { th: 'บันทึกไม่สำเร็จ กรุณาลองอีกครั้ง', en: 'Failed to save — please try again' },
  'error.delete_failed':    { th: 'ลบไม่สำเร็จ กรุณาลองอีกครั้ง', en: 'Failed to delete — please try again' },
  'error.no_permission':    { th: 'คุณไม่มีสิทธิ์ทำรายการนี้ ติดต่อผู้ดูแลระบบหากต้องการสิทธิ์', en: 'You do not have permission for this action' },
  'error.not_found':        { th: 'ไม่พบข้อมูลที่ระบุ — อาจถูกลบไปแล้ว', en: 'Item not found — it may have been deleted' },
  'error.network':          { th: 'เกิดปัญหาเชื่อมต่อ กรุณาตรวจสอบอินเทอร์เน็ตและลองอีกครั้ง', en: 'Connection problem — check your internet and try again' },

  // ─── Misc ───
  'units.questions':        { th: 'ข้อ', en: 'questions' },
  'units.people':           { th: 'คน', en: 'people' },
  'units.items':            { th: 'รายการ', en: 'items' },
  'time.just_now':          { th: 'เมื่อสักครู่', en: 'just now' },
  'time.minutes_ago':       { th: 'นาทีที่แล้ว', en: 'minutes ago' },
  'time.hours_ago':         { th: 'ชั่วโมงที่แล้ว', en: 'hours ago' },
  'time.days_ago':          { th: 'วันที่แล้ว', en: 'days ago' },

  // ─── Questions page ───
  'q.create_new':           { th: 'สร้างข้อสอบใหม่', en: 'Create new question' },
  'q.empty_desc':           { th: 'ใช้ตัวกรองข้างบนเพื่อค้นหา หรือสร้างข้อสอบใหม่', en: 'Use the filters above or create a new question' },
  'q.aria.view':            { th: 'ดูรายละเอียด', en: 'View detail' },
  'q.aria.edit':            { th: 'แก้ไข', en: 'Edit' },
  'q.aria.download':        { th: 'ดาวน์โหลด', en: 'Download' },
  'q.aria.delete':          { th: 'ลบ', en: 'Delete' },
  'q.total':                { th: 'ทั้งหมด', en: 'Total' },

  // ─── Question Bank ───
  'qbank.title':            { th: 'ธนาคารข้อสอบ', en: 'Question bank' },
  'qbank.subtitle':         { th: 'ค้นหาและส่งออกข้อสอบที่ผ่านการอนุมัติแล้ว', en: 'Search and export approved questions' },
  'qbank.search':           { th: 'ค้นหาเนื้อหา...', en: 'Search content...' },
  'qbank.empty':            { th: 'ไม่พบข้อสอบ', en: 'No questions found' },
  'qbank.empty_desc':       { th: 'ลองล้างตัวกรอง หรือพิมพ์คำค้นหาอื่น', en: 'Try clearing filters or different keywords' },
  'qbank.in_cart':          { th: 'ในตะกร้า', en: 'In cart' },
  'qbank.add':              { th: 'เพิ่ม', en: 'Add' },
  'qbank.clear_cart':       { th: 'ล้างตะกร้า', en: 'Clear cart' },
  'qbank.export':           { th: 'ส่งออก', en: 'Export' },
  'qbank.exporting':        { th: 'กำลังส่งออก', en: 'Exporting' },
  'qbank.select_first':     { th: 'กรุณาเลือกข้อสอบก่อน', en: 'Please select questions first' },
  'qbank.export_zip':       { th: 'แยกตามประเภท (ZIP)', en: 'By type (ZIP)' },
  'qbank.export_zip_desc':  { th: 'ส่งออก CSV แยกไฟล์ตามประเภทคำถาม', en: 'Export CSVs split by question type' },

  // ─── Approvals ───
  'approvals.title':        { th: 'อนุมัติข้อสอบ', en: 'Approvals' },
  'approvals.subtitle':     { th: 'ตรวจสอบและอนุมัติข้อสอบที่ครูส่งมา', en: 'Review and approve teacher submissions' },
  'approvals.pending_count':{ th: 'รออนุมัติ', en: 'Pending' },
  'approvals.search':       { th: 'ค้นหาคำถาม...', en: 'Search questions...' },
  'approvals.col.question': { th: 'คำถาม', en: 'Question' },
  'approvals.col.creator':  { th: 'ผู้สร้าง', en: 'Creator' },
  'approvals.col.subject':  { th: 'วิชา', en: 'Subject' },
  'approvals.col.grade':    { th: 'ระดับชั้น', en: 'Grade' },
  'approvals.col.submitted':{ th: 'วันที่ส่ง', en: 'Submitted' },
  'approvals.col.actions':  { th: 'การดำเนินการ', en: 'Actions' },
  'approvals.empty':        { th: 'ไม่มีข้อสอบรออนุมัติ', en: 'No questions pending approval' },
  'approvals.empty_desc':   { th: 'ดีมาก! ทุกข้อสอบได้รับการตรวจสอบเรียบร้อยแล้ว', en: 'Great! All questions have been reviewed.' },
  'approvals.selected':     { th: 'เลือกอยู่', en: 'selected' },
  'approvals.approve_selected': { th: 'อนุมัติที่เลือก', en: 'Approve selected' },
  'approvals.reject_selected':  { th: 'ปฏิเสธที่เลือก', en: 'Reject selected' },
  'approvals.bulk_approve_title': { th: 'อนุมัติข้อสอบ', en: 'Approve questions' },
  'approvals.bulk_approve_desc':  { th: 'ระบบจะส่งข้อสอบทั้งหมดที่เลือกเข้าสู่ธนาคารข้อสอบ', en: 'All selected questions will be moved to the question bank.' },
  'approvals.bulk_approve_confirm': { th: 'อนุมัติทั้งหมด', en: 'Approve all' },
  'approvals.approve_one':  { th: 'อนุมัติข้อสอบนี้?', en: 'Approve this question?' },
  'approvals.reject_modal_title': { th: 'ปฏิเสธข้อสอบ', en: 'Reject questions' },
  'approvals.reject_modal_desc':  { th: 'ครูจะเห็นเหตุผลนี้และแก้ไขข้อสอบใหม่ได้', en: 'The teacher will see this reason and can revise.' },
  'approvals.reject_reason_label': { th: 'เหตุผลในการปฏิเสธ', en: 'Rejection reason' },
  'approvals.reject_reason_ph':    { th: 'เช่น คำถามไม่ชัดเจน, ตัวเลือกซ้ำกัน...', en: 'e.g. Unclear question, duplicate choices...' },
  'approvals.reject_reason_required': { th: 'กรุณาระบุเหตุผลการปฏิเสธ', en: 'Please provide a rejection reason' },
  'approvals.confirm_reject': { th: 'ยืนยันปฏิเสธ', en: 'Confirm reject' },
  'approvals.success_approve': { th: 'อนุมัติเรียบร้อย', en: 'Approved successfully' },
  'approvals.fail_approve':    { th: 'อนุมัติไม่สำเร็จ', en: 'Failed to approve' },
  'approvals.success_reject':  { th: 'ปฏิเสธเรียบร้อย', en: 'Rejected successfully' },
  'approvals.fail_reject':     { th: 'ปฏิเสธไม่สำเร็จ', en: 'Failed to reject' },
  'approvals.items':           { th: 'รายการ', en: 'items' },

  // ─── Users page ───
  'users.title':            { th: 'จัดการผู้ใช้', en: 'User management' },
  'users.add':              { th: 'เพิ่มผู้ใช้', en: 'Add user' },
  'users.search':           { th: 'ค้นหาชื่อ, username, email...', en: 'Search name, username, email...' },
  'users.col.user':         { th: 'ผู้ใช้', en: 'User' },
  'users.col.email':        { th: 'อีเมล', en: 'Email' },
  'users.col.role':         { th: 'บทบาท', en: 'Role' },
  'users.col.status':       { th: 'สถานะ', en: 'Status' },
  'users.col.actions':      { th: 'การดำเนินการ', en: 'Actions' },
  'users.empty':            { th: 'ไม่พบผู้ใช้', en: 'No users found' },
  'users.status_active':    { th: 'ใช้งานได้', en: 'Active' },
  'users.status_inactive':  { th: 'ปิดใช้งาน', en: 'Inactive' },
  'users.enable':           { th: 'เปิดใช้งาน', en: 'Enable' },
  'users.disable':          { th: 'ปิดใช้งาน', en: 'Disable' },
  'users.delete_confirm':   { th: 'ลบผู้ใช้', en: 'Delete user' },
  'users.delete_desc':      { th: 'การลบจะถูกบันทึกใน activity log', en: 'Deletion will be recorded in the activity log' },
  'users.modal_edit':       { th: 'แก้ไขผู้ใช้', en: 'Edit user' },
  'users.modal_add':        { th: 'เพิ่มผู้ใช้ใหม่', en: 'Add new user' },
  'users.field.username':   { th: 'ชื่อผู้ใช้', en: 'Username' },
  'users.field.fullname':   { th: 'ชื่อ-นามสกุล', en: 'Full name' },
  'users.field.email':      { th: 'อีเมล', en: 'Email' },
  'users.field.role':       { th: 'บทบาท', en: 'Role' },
  'users.field.password':   { th: 'รหัสผ่าน', en: 'Password' },
  'users.field.password_new': { th: 'รหัสผ่านใหม่ (เว้นว่างหากไม่เปลี่ยน)', en: 'New password (leave blank to keep)' },
  'users.field.password_hint_new': { th: 'เว้นว่างหากไม่ต้องการเปลี่ยน', en: 'Leave blank to keep current password' },
  'users.field.password_hint': { th: 'อย่างน้อย 8 ตัวอักษร', en: 'At least 8 characters' },
  'users.success_save':     { th: 'แก้ไขผู้ใช้เรียบร้อย', en: 'User updated' },
  'users.success_add':      { th: 'เพิ่มผู้ใช้เรียบร้อย', en: 'User added' },
  'users.success_status':   { th: 'อัปเดตสถานะแล้ว', en: 'Status updated' },
  'users.success_delete':   { th: 'ลบผู้ใช้เรียบร้อย', en: 'User deleted' },

  // ─── Metadata page ───
  'metadata.title':         { th: 'ข้อมูลพื้นฐาน', en: 'Metadata' },
  'metadata.desc':          { th: 'จัดการกลุ่มสาระ ระดับชั้น ระดับความยาก และระดับการคิด', en: 'Manage subjects, grades, difficulty, and cognitive levels' },
  'metadata.tab.subjects':  { th: 'กลุ่มสาระ', en: 'Subjects' },
  'metadata.tab.grades':    { th: 'ระดับชั้น', en: 'Grades' },
  'metadata.tab.difficulty':{ th: 'ระดับความยาก', en: 'Difficulty' },
  'metadata.tab.cognitive': { th: 'ระดับการคิด (Bloom)', en: 'Cognitive (Bloom)' },
  'metadata.add_new':       { th: 'เพิ่มใหม่', en: 'Add new' },
  'metadata.irreversible':  { th: 'การกระทำนี้ย้อนกลับไม่ได้', en: 'This action cannot be undone' },
  'metadata.modal.subject_add':   { th: 'เพิ่มกลุ่มสาระใหม่', en: 'Add subject group' },
  'metadata.modal.subject_edit':  { th: 'แก้ไขกลุ่มสาระ', en: 'Edit subject group' },
  'metadata.modal.grade_add':     { th: 'เพิ่มระดับชั้นใหม่', en: 'Add grade level' },
  'metadata.modal.grade_edit':    { th: 'แก้ไขระดับชั้น', en: 'Edit grade level' },
  'metadata.modal.difficulty_add':  { th: 'เพิ่มระดับความยากใหม่', en: 'Add difficulty level' },
  'metadata.modal.difficulty_edit': { th: 'แก้ไขระดับความยาก', en: 'Edit difficulty level' },
  'metadata.modal.cognitive_add':   { th: 'เพิ่มระดับการคิดใหม่', en: 'Add cognitive level' },
  'metadata.modal.cognitive_edit':  { th: 'แก้ไขระดับการคิด', en: 'Edit cognitive level' },
  'metadata.field.subject_name':    { th: 'ชื่อกลุ่มสาระ *', en: 'Subject name *' },
  'metadata.field.code':            { th: 'รหัส', en: 'Code' },
  'metadata.field.description':     { th: 'คำอธิบาย', en: 'Description' },
  'metadata.field.sort_order':      { th: 'ลำดับ', en: 'Sort order' },
  'metadata.field.grade_name':      { th: 'ชื่อระดับชั้น *', en: 'Grade name *' },
  'metadata.field.level_order':     { th: 'ลำดับ *', en: 'Order *' },
  'metadata.field.level_code':      { th: 'รหัส', en: 'Code' },
  'metadata.field.name':            { th: 'ชื่อ *', en: 'Name *' },
  'metadata.field.color':           { th: 'สี', en: 'Color' },
  'metadata.field.th_name':         { th: 'ชื่อภาษาไทย *', en: 'Thai name *' },
  'metadata.field.en_name':         { th: 'ชื่อภาษาอังกฤษ', en: 'English name' },
  'metadata.field.bloom':           { th: 'ระดับ Bloom (1-6) *', en: 'Bloom level (1-6) *' },
  'metadata.empty.subjects':        { th: 'ยังไม่มีกลุ่มสาระ', en: 'No subjects yet' },
  'metadata.empty.grades':          { th: 'ยังไม่มีระดับชั้น', en: 'No grades yet' },
  'metadata.empty.difficulty':      { th: 'ยังไม่มีระดับความยาก', en: 'No difficulty levels yet' },
  'metadata.empty.cognitive':       { th: 'ยังไม่มีระดับการคิด', en: 'No cognitive levels yet' },
  'metadata.success_add':           { th: 'เพิ่มเรียบร้อย', en: 'Added successfully' },
  'metadata.success_edit':          { th: 'แก้ไขเรียบร้อย', en: 'Updated successfully' },
  'metadata.success_delete':        { th: 'ลบเรียบร้อย', en: 'Deleted successfully' },

  // ─── Learning Indicators ───
  'learning.title':         { th: 'จัดการตัวชี้วัด', en: 'Learning indicators' },
  'learning.desc':          { th: 'โครงสร้าง: กลุ่มสาระ → สาระ → มาตรฐาน → ตัวชี้วัด', en: 'Structure: Subject → Strand → Standard → Indicator' },
  'learning.search':        { th: 'ค้นหากลุ่มสาระ...', en: 'Search subjects...' },
  'learning.col.subject':   { th: 'กลุ่มสาระ', en: 'Subject group' },
  'learning.col.strand':    { th: 'สาระ', en: 'Strand' },
  'learning.col.standard':  { th: 'มาตรฐาน', en: 'Standard' },
  'learning.col.indicator': { th: 'ตัวชี้วัด', en: 'Indicator' },
  'learning.select_subject':{ th: 'เลือกกลุ่มสาระก่อน', en: 'Select a subject first' },
  'learning.select_strand': { th: 'เลือกสาระก่อน', en: 'Select a strand first' },
  'learning.select_standard':{ th: 'เลือกมาตรฐานก่อน', en: 'Select a standard first' },
  'learning.choose_subject':{ th: 'เลือกกลุ่มสาระ', en: 'Choose subject' },
  'learning.choose_strand': { th: 'เลือกสาระ', en: 'Choose strand' },
  'learning.choose_standard':{ th: 'เลือกมาตรฐาน', en: 'Choose standard' },
  'learning.indicator_label': { th: 'ตัวชี้วัด', en: 'Indicator' },
  'learning.empty':         { th: 'ว่างเปล่า', en: 'Empty' },
  'learning.no_strand':     { th: 'ยังไม่มีสาระ', en: 'No strands yet' },
  'learning.no_standard':   { th: 'ยังไม่มีมาตรฐาน', en: 'No standards yet' },
  'learning.no_indicator':  { th: 'ยังไม่มีตัวชี้วัด', en: 'No indicators yet' },
  'learning.strand_modal_add':  { th: 'เพิ่มสาระใหม่', en: 'Add strand' },
  'learning.strand_modal_edit': { th: 'แก้ไขสาระ', en: 'Edit strand' },
  'learning.standard_modal_add':  { th: 'เพิ่มมาตรฐานใหม่', en: 'Add standard' },
  'learning.standard_modal_edit': { th: 'แก้ไขมาตรฐาน', en: 'Edit standard' },
  'learning.indicator_modal_add':  { th: 'เพิ่มตัวชี้วัดใหม่', en: 'Add indicator' },
  'learning.indicator_modal_edit': { th: 'แก้ไขตัวชี้วัด', en: 'Edit indicator' },
  'learning.field.strand_number':  { th: 'ลำดับสาระ', en: 'Strand number' },
  'learning.field.strand_name':    { th: 'ชื่อสาระ *', en: 'Strand name *' },
  'learning.field.standard_code':  { th: 'รหัส *', en: 'Code *' },
  'learning.field.standard_name':  { th: 'ชื่อมาตรฐาน *', en: 'Standard name *' },
  'learning.field.grade':          { th: 'ระดับชั้น', en: 'Grade' },
  'learning.field.indicator_code': { th: 'รหัสตัวชี้วัด *', en: 'Indicator code *' },
  'learning.field.indicator_title':{ th: 'ชื่อตัวชี้วัด *', en: 'Indicator title *' },
  'learning.field.grade_required': { th: 'ระดับชั้น *', en: 'Grade *' },
  'learning.grade_all':     { th: '— ไม่ระบุ (ทุกชั้น) —', en: '— Any grade —' },
  'learning.grade_choose':  { th: '— เลือกชั้น —', en: '— Choose grade —' },
  'learning.delete_strand': { th: 'มาตรฐานและตัวชี้วัดในสาระนี้จะหายไปด้วย', en: 'Standards and indicators in this strand will also be deleted' },
  'learning.delete_standard': { th: 'ตัวชี้วัดในมาตรฐานนี้จะหายไปด้วย', en: 'Indicators in this standard will also be deleted' },
  'learning.reorder_success': { th: 'เรียงลำดับใหม่แล้ว', en: 'Reordered' },
  'learning.reorder_fail':    { th: 'เรียงลำดับไม่สำเร็จ', en: 'Failed to reorder' },
  'learning.save_success':    { th: 'บันทึกการแก้ไขเรียบร้อย', en: 'Saved successfully' },
  'learning.add_success':     { th: 'เพิ่มเรียบร้อย', en: 'Added successfully' },
  'learning.delete_success':  { th: 'ลบเรียบร้อย', en: 'Deleted' },
  'learning.strand_unit':     { th: 'สาระ', en: 'strands' },
  'learning.standard_unit':   { th: 'มาตรฐาน', en: 'standards' },
  'learning.indicator_unit':  { th: 'ตัว', en: 'indicators' },

  // ─── Reports ───
  'reports.title':          { th: 'รายงานระบบ', en: 'Reports' },
  'reports.desc':           { th: 'วิเคราะห์สถิติและข้อมูลของระบบธนาคารข้อสอบ', en: 'Analyze statistics and data of the question bank' },
  'reports.tab.overview':   { th: 'ภาพรวม', en: 'Overview' },
  'reports.tab.by_subject': { th: 'ตามกลุ่มสาระ', en: 'By subject' },
  'reports.tab.by_grade':   { th: 'ตามระดับชั้น', en: 'By grade' },
  'reports.stat.total':     { th: 'ข้อสอบทั้งหมด', en: 'Total questions' },
  'reports.stat.approved':  { th: 'อนุมัติแล้ว', en: 'Approved' },
  'reports.stat.pending':   { th: 'รออนุมัติ', en: 'Pending' },
  'reports.stat.users':     { th: 'ผู้ใช้ในระบบ', en: 'Users' },
  'reports.status_breakdown': { th: 'สถานะข้อสอบ', en: 'Question status' },
  'reports.by_subject_title': { th: 'ข้อสอบตามกลุ่มสาระ', en: 'Questions by subject' },
  'reports.by_grade_title':   { th: 'ข้อสอบตามระดับชั้น', en: 'Questions by grade' },
  'reports.no_data':          { th: 'ยังไม่มีข้อมูล', en: 'No data yet' },
  'reports.status.approved':  { th: 'อนุมัติแล้ว', en: 'Approved' },
  'reports.status.pending':   { th: 'รออนุมัติ', en: 'Pending' },
  'reports.status.draft':     { th: 'ร่าง', en: 'Draft' },
  'reports.status.rejected':  { th: 'ไม่อนุมัติ', en: 'Rejected' },

  // ─── Activity Log ───
  'activity.title':         { th: 'กิจกรรมในระบบ', en: 'Activity log' },
  'activity.desc':          { th: 'บันทึกการกระทำของผู้ใช้', en: 'Record of user actions' },
  'activity.empty':         { th: 'ยังไม่มีกิจกรรม', en: 'No activity yet' },
  'activity.empty_desc':    { th: 'กิจกรรมในระบบจะปรากฏที่นี่เมื่อมีผู้ใช้สร้าง/แก้ไข/อนุมัติข้อสอบ', en: 'Activity will appear here when users create, edit, or approve questions' },

  // ─── Backup ───
  'backup.title':           { th: 'จัดการ Backup', en: 'Backup management' },
  'backup.desc':            { th: 'สำรองข้อมูลฐานข้อมูลทั้งหมด', en: 'Back up the entire database' },
  'backup.create':          { th: 'สร้าง Backup ใหม่', en: 'Create new backup' },
  'backup.create_confirm':  { th: 'สร้าง backup ใหม่ตอนนี้?', en: 'Create a new backup now?' },
  'backup.create_desc':     { th: 'อาจใช้เวลา 1-2 นาทีหากฐานข้อมูลใหญ่', en: 'May take 1-2 minutes for large databases' },
  'backup.start':           { th: 'เริ่มสำรอง', en: 'Start backup' },
  'backup.col.file':        { th: 'ไฟล์', en: 'File' },
  'backup.col.size':        { th: 'ขนาด', en: 'Size' },
  'backup.col.tables':      { th: 'ตาราง', en: 'Tables' },
  'backup.col.created':     { th: 'สร้างเมื่อ', en: 'Created' },
  'backup.col.actions':     { th: 'การดำเนินการ', en: 'Actions' },
  'backup.empty':           { th: 'ยังไม่มี backup', en: 'No backups yet' },
  'backup.empty_desc':      { th: "กดปุ่ม 'สร้าง Backup ใหม่' เพื่อสำรองข้อมูลครั้งแรก", en: "Click 'Create new backup' to save your first backup" },
  'backup.create_now':      { th: 'สร้างตอนนี้', en: 'Create now' },
  'backup.delete_confirm':  { th: 'การลบนี้ไม่สามารถย้อนกลับได้', en: 'This cannot be undone' },
  'backup.table_unit':      { th: 'ตาราง', en: 'tables' },
  'backup.success_create':  { th: 'สร้าง backup เรียบร้อย', en: 'Backup created' },
  'backup.fail_create':     { th: 'สร้าง backup ไม่สำเร็จ', en: 'Backup failed' },
  'backup.success_delete':  { th: 'ลบ backup เรียบร้อย', en: 'Backup deleted' },

  // ─── Subject Permissions ───
  'subjperm.title':         { th: 'จัดการสิทธิ์กลุ่มสาระ', en: 'Subject permissions' },
  'subjperm.desc':          { th: 'กำหนดให้ครูเข้าถึงกลุ่มสาระวิชาใดได้บ้าง', en: 'Assign which subjects teachers can access' },
  'subjperm.manage':        { th: 'จัดการสมาชิก', en: 'Manage members' },
  'subjperm.no_members':    { th: '— ยังไม่มีสมาชิก —', en: '— No members yet —' },
  'subjperm.questions_count': { th: 'ข้อสอบในกลุ่มนี้', en: 'questions in this group' },
  'subjperm.modal_title':   { th: 'จัดการสมาชิก', en: 'Manage members' },
  'subjperm.modal_desc':    { th: 'ติ๊กเลือกครูที่จะให้เข้าถึงกลุ่มสาระนี้', en: 'Check the teachers who should access this subject group' },
  'subjperm.search':        { th: 'ค้นหาชื่อหรือ username...', en: 'Search name or username...' },
  'subjperm.no_users':      { th: 'ยังไม่มีครูในระบบ', en: 'No teachers in system' },
  'subjperm.no_search':     { th: 'ไม่พบที่ตรงกับคำค้น', en: 'No results for search' },
  'subjperm.no_changes':    { th: 'ยังไม่มีการเปลี่ยนแปลง', en: 'No changes yet' },
  'subjperm.save_changes':  { th: 'บันทึก', en: 'Save' },
  'subjperm.success_save':  { th: 'บันทึกสมาชิกเรียบร้อย', en: 'Members saved' },
  'subjperm.empty':         { th: 'ยังไม่มีกลุ่มสาระ', en: 'No subject groups' },
  'subjperm.empty_desc':    { th: 'ไปเพิ่มที่หน้า ข้อมูลพื้นฐาน → กลุ่มสาระ', en: 'Add groups in Metadata → Subjects' },
  'subjperm.load_fail':     { th: 'โหลดข้อมูลไม่สำเร็จ ลองรีเฟรชหน้านี้อีกครั้ง', en: 'Failed to load data — try refreshing' },
  'subjperm.people_unit':   { th: 'คน', en: 'members' },

  // ─── Wizard question types ───
  'wizard.type.single_choice':      { th: 'ปรนัย (เลือกตอบ 1 ข้อ)', en: 'Single choice (1 answer)' },
  'wizard.type.single_choice_desc': { th: 'A, B, C, D — เลือกตอบเดียว', en: 'A, B, C, D — one correct answer' },
  'wizard.type.multiple_answer':    { th: 'ปรนัย (เลือกหลายข้อ)', en: 'Multiple answer' },
  'wizard.type.multiple_answer_desc': { th: 'ติ๊กได้มากกว่า 1 ตัวเลือก', en: 'More than one correct answer' },
  'wizard.type.true_false':         { th: 'ถูก / ผิด', en: 'True / False' },
  'wizard.type.true_false_desc':    { th: 'ข้อสอบจริง-เท็จ', en: 'True or false question' },
  'wizard.type.matching':           { th: 'จับคู่', en: 'Matching' },
  'wizard.type.matching_desc':      { th: 'จับคู่ฝั่งซ้ายกับฝั่งขวา', en: 'Match left with right' },
  'wizard.type.essay':              { th: 'อัตนัย (เขียนตอบ)', en: 'Essay (written answer)' },
  'wizard.type.essay_desc':         { th: 'เขียนคำตอบเอง พร้อมเฉลยและเกณฑ์', en: 'Write answer with model and criteria' },

  // ─── Wizard ───
  'wizard.title_new':       { th: 'สร้างข้อสอบใหม่', en: 'Create new question' },
  'wizard.title_edit':      { th: 'แก้ไขข้อสอบ', en: 'Edit question' },
  'wizard.desc_new':        { th: 'กรอกข้อมูลทีละขั้น — ระบบจะบันทึกอัตโนมัติเมื่อกดเปลี่ยนขั้น', en: 'Fill in step by step — auto-saved on step change' },
  'wizard.breadcrumb_my_q': { th: 'ข้อสอบของฉัน', en: 'My questions' },
  'wizard.breadcrumb_new':  { th: 'สร้างใหม่', en: 'New' },
  'wizard.exit':            { th: 'ออกจาก Wizard', en: 'Exit wizard' },
  'wizard.exit_confirm':    { th: 'ยกเลิกการสร้างข้อสอบ?', en: 'Cancel question creation?' },
  'wizard.exit_desc':       { th: 'ข้อมูลที่กรอกไว้จะหายทั้งหมด', en: 'All entered data will be lost' },
  'wizard.autosave':        { th: 'บันทึกแบบร่างอัตโนมัติเมื่อ', en: 'Auto-saved draft at' },
  'wizard.step.type':       { th: 'ประเภทข้อสอบ', en: 'Question type' },
  'wizard.step.basic':      { th: 'ข้อมูลพื้นฐาน', en: 'Basic info' },
  'wizard.step.content':    { th: 'เนื้อหาคำถาม', en: 'Question content' },
  'wizard.step.answers':    { th: 'คำตอบ', en: 'Answers' },
  'wizard.step.review':     { th: 'ตรวจสอบ', en: 'Review' },
  'wizard.step.type_desc':  { th: 'เลือกรูปแบบ', en: 'Choose format' },
  'wizard.step.basic_desc': { th: 'วิชา ระดับชั้น ตัวชี้วัด', en: 'Subject, grade, indicators' },
  'wizard.step.content_desc':{ th: 'คำถาม + รูป', en: 'Question + image' },
  'wizard.step.answers_desc':{ th: 'ตัวเลือก / คู่ / เฉลย', en: 'Choices / pairs / answer' },
  'wizard.step.review_desc':{ th: 'ดูครั้งสุดท้าย', en: 'Final check' },
  'wizard.type_section':    { th: 'เลือกประเภทข้อสอบ', en: 'Choose question type' },
  'wizard.type_subtitle':   { th: 'ระบบจะแสดงรายละเอียดให้กรอกตามประเภท', en: 'The system will show fields based on the selected type' },
  'wizard.basic_section':   { th: 'ข้อมูลพื้นฐาน', en: 'Basic information' },
  'wizard.basic_subtitle':  { th: 'กลุ่มสาระและระดับชั้นจะกำหนดตัวชี้วัดที่เลือกได้', en: 'Subject and grade determine available indicators' },
  'wizard.content_section': { th: 'เนื้อหาคำถาม', en: 'Question content' },
  'wizard.content_subtitle':{ th: 'พิมพ์คำถามและแนบรูป (ถ้ามี)', en: 'Type the question and attach an image (optional)' },
  'wizard.answer_section':  { th: 'ตัวเลือกคำตอบ', en: 'Answer choices' },
  'wizard.review_section':  { th: 'ตรวจสอบข้อมูล', en: 'Review data' },
  'wizard.review_subtitle': { th: 'อ่านอีกครั้งก่อนบันทึก', en: 'Read once more before saving' },
  'wizard.save_draft':      { th: 'บันทึกเป็นร่าง', en: 'Save as draft' },
  'wizard.send_approval':   { th: 'ส่งเพื่อขออนุมัติ', en: 'Submit for approval' },
  'wizard.confirm_label':   { th: 'ฉันได้ตรวจสอบข้อมูลทั้งหมดแล้ว และยืนยันว่าถูกต้อง', en: 'I have reviewed all information and confirm it is correct' },
  'wizard.topic_label':     { th: 'หัวข้อ / เรื่อง *', en: 'Topic *' },
  'wizard.subject_label':   { th: 'กลุ่มสาระการเรียนรู้ *', en: 'Subject group *' },
  'wizard.grade_label':     { th: 'ระดับชั้น *', en: 'Grade *' },
  'wizard.indicator_label': { th: 'ตัวชี้วัด', en: 'Indicators' },
  'wizard.indicator_hint':  { th: 'เลือกกลุ่มสาระและระดับชั้นก่อน เพื่อโหลดตัวชี้วัด', en: 'Select subject and grade first to load indicators' },
  'wizard.indicator_loading':{ th: 'กำลังโหลด...', en: 'Loading...' },
  'wizard.indicator_empty': { th: 'ไม่มีตัวชี้วัดสำหรับชั้น/วิชานี้', en: 'No indicators for this grade/subject' },
  'wizard.indicator_search':{ th: 'ค้นหาตัวชี้วัด...', en: 'Search indicators...' },
  'wizard.indicator_no_match': { th: 'ไม่พบตัวชี้วัดที่ตรงกับคำค้น', en: 'No matching indicators' },
  'wizard.bloom_label':     { th: 'ระดับการคิด (Bloom)', en: 'Cognitive level (Bloom)' },
  'wizard.difficulty_label':{ th: 'ระดับความยาก', en: 'Difficulty level' },
  'wizard.question_label':  { th: 'คำถาม *', en: 'Question *' },
  'wizard.question_ph':     { th: 'พิมพ์คำถามที่ต้องการ... (อย่างน้อย 10 ตัวอักษร)', en: 'Type your question... (at least 10 characters)' },
  'wizard.points_label':    { th: 'คะแนนเต็ม', en: 'Full score' },
  'wizard.image_label':     { th: 'รูปประกอบ (ไม่บังคับ)', en: 'Image (optional)' },
  'wizard.image_click':     { th: 'คลิกเพื่อเลือกรูป', en: 'Click to choose image' },
  'wizard.image_uploading': { th: 'กำลังอัปโหลด...', en: 'Uploading...' },
  'wizard.image_size':      { th: 'JPG, PNG, GIF, WEBP — สูงสุด 5MB', en: 'JPG, PNG, GIF, WEBP — max 5 MB' },
  'wizard.image_err_type':  { th: 'กรุณาเลือกไฟล์รูปภาพ', en: 'Please choose an image file' },
  'wizard.image_err_type_hint': { th: 'รองรับ: JPG, PNG, GIF, WEBP', en: 'Supported: JPG, PNG, GIF, WEBP' },
  'wizard.image_err_size':  { th: 'ไฟล์ใหญ่เกิน 5MB', en: 'File exceeds 5 MB' },
  'wizard.image_err_size_hint': { th: 'กรุณาเลือกรูปที่เล็กกว่า', en: 'Please choose a smaller image' },
  'wizard.image_success':   { th: 'อัปโหลดรูปสำเร็จ', en: 'Image uploaded' },
  'wizard.image_fail':      { th: 'อัปโหลดไม่สำเร็จ', en: 'Upload failed' },
  'wizard.choice_single_subtitle': { th: 'ติ๊กตัวเลือกที่ถูก (เลือกได้ 1 ข้อ)', en: 'Check the correct choice (select one)' },
  'wizard.choice_multi_subtitle':  { th: 'ติ๊กตัวเลือกที่ถูก (เลือกได้หลายข้อ)', en: 'Check all correct choices (select multiple)' },
  'wizard.true_section':    { th: 'คำตอบ ถูก / ผิด', en: 'True / False answer' },
  'wizard.true_subtitle':   { th: 'เลือกว่าคำตอบที่ถูกต้องคือ ถูกหรือผิด', en: 'Select whether the correct answer is true or false' },
  'wizard.matching_section':{ th: 'คู่จับคู่', en: 'Matching pairs' },
  'wizard.matching_subtitle':{ th: 'ระบุข้อความฝั่งซ้ายและคำตอบที่จับคู่ฝั่งขวา (อย่างน้อย 2 คู่)', en: 'Specify left text and matching right text (minimum 2 pairs)' },
  'wizard.essay_section':   { th: 'ข้อสอบอัตนัย', en: 'Essay question' },
  'wizard.essay_subtitle':  { th: 'ระบุเฉลย/ตัวอย่างคำตอบ และเกณฑ์การให้คะแนน', en: 'Provide model answer and scoring criteria' },
  'wizard.essay_answer_label': { th: 'ตัวอย่างคำตอบ / เฉลย *', en: 'Model answer *' },
  'wizard.essay_criteria_label': { th: 'เกณฑ์การให้คะแนน (ไม่บังคับ)', en: 'Scoring criteria (optional)' },
  'wizard.explanation_label':  { th: 'คำอธิบาย / เฉลย (ไม่บังคับ)', en: 'Explanation (optional)' },
  'wizard.add_choice':      { th: 'เพิ่มตัวเลือก', en: 'Add choice' },
  'wizard.add_pair':        { th: 'เพิ่มคู่', en: 'Add pair' },
  'wizard.choice_correct_expl': { th: 'คำอธิบายเฉลย (ไม่บังคับ)', en: 'Answer explanation (optional)' },
  'wizard.no_type':         { th: 'กรุณาเลือกประเภทข้อสอบก่อน', en: 'Please select a question type first' },
  'wizard.selected_label':  { th: 'เลือกแล้ว', en: 'Selected' },
  'wizard.topic_prefix':    { th: 'หัวข้อ:', en: 'Topic:' },
  'wizard.val.type':        { th: 'กรุณาเลือกประเภทข้อสอบ', en: 'Please choose a question type' },
  'wizard.val.subject':     { th: 'กรุณาเลือกกลุ่มสาระ', en: 'Please select a subject group' },
  'wizard.val.grade':       { th: 'กรุณาเลือกระดับชั้น', en: 'Please select a grade level' },
  'wizard.val.topic':       { th: 'กรุณาระบุหัวข้อ/เรื่อง', en: 'Please enter a topic' },
  'wizard.val.question':    { th: 'กรุณาใส่คำถาม', en: 'Please enter a question' },
  'wizard.val.question_short': { th: 'คำถามต้องยาวอย่างน้อย 10 ตัวอักษร', en: 'Question must be at least 10 characters' },
  'wizard.val.pairs':       { th: 'กรุณาระบุคู่จับคู่อย่างน้อย 2 คู่', en: 'Please specify at least 2 matching pairs' },
  'wizard.val.essay':       { th: 'กรุณาระบุเฉลย/ตัวอย่างคำตอบ', en: 'Please provide a model answer' },
  'wizard.val.choices_min': { th: 'กรุณาระบุตัวเลือกอย่างน้อย 2 ข้อ', en: 'Please provide at least 2 answer choices' },
  'wizard.val.choices_correct': { th: 'กรุณาระบุคำตอบที่ถูกอย่างน้อย 1 ข้อ', en: 'Please mark at least 1 correct answer' },
  'wizard.val.confirm':     { th: 'กรุณายืนยันว่าได้ตรวจสอบข้อมูลแล้ว', en: 'Please confirm you have reviewed the data' },
  'wizard.success_update_submit': { th: 'อัปเดตและส่งเข้าอนุมัติแล้ว', en: 'Updated and submitted for approval' },
  'wizard.success_update':  { th: 'อัปเดตเรียบร้อย', en: 'Updated successfully' },
  'wizard.success_submit':  { th: 'ส่งเข้าสู่กระบวนการอนุมัติแล้ว', en: 'Submitted for approval' },
  'wizard.success_draft':   { th: 'บันทึกร่างเรียบร้อย', en: 'Draft saved' },
  'wizard.char_count':      { th: 'ตัวอักษร', en: 'characters' },
  'wizard.symbol_hint':     { th: 'แทรกสัญลักษณ์ทางคณิตได้จากแถบด้านบน', en: 'Insert math symbols from the bar above' },

  // ─── Import modal ───
  'import.title':           { th: 'นำเข้าข้อสอบจาก CSV', en: 'Import questions from CSV' },
  'import.desc':            { th: 'อัปโหลดไฟล์ CSV ตาม template ด้านล่าง — ระบบจะตรวจสอบก่อนนำเข้า', en: 'Upload a CSV file matching the template below — the system will validate before importing' },
  'import.template_title':  { th: 'ดาวน์โหลด Template (CSV)', en: 'Download Template (CSV)' },
  'import.template_desc':   { th: 'เลือก template ตามประเภทข้อสอบ — กรอกข้อมูลใน Excel/Numbers แล้วอัพไฟล์กลับมา', en: 'Choose a template by question type, fill in Excel/Numbers, then upload it back' },
  'import.click_to_select': { th: 'คลิกเพื่อเลือกไฟล์ CSV', en: 'Click to choose CSV file' },
  'import.file_hint':       { th: 'รองรับ .csv (UTF-8) สูงสุด 5MB', en: 'Supports .csv (UTF-8) up to 5 MB' },
  'import.change_file':     { th: 'เปลี่ยน', en: 'Change' },
  'import.row':             { th: 'แถว', en: 'Row' },
  'import.all_rows':        { th: 'ทั้งหมด', en: 'Total' },
  'import.valid_rows':      { th: 'ถูกต้อง', en: 'Valid' },
  'import.invalid_rows':    { th: 'ผิดพลาด', en: 'Errors' },
  'import.do_import':       { th: 'นำเข้า', en: 'Import' },
  'import.success':         { th: 'นำเข้าสำเร็จ', en: 'Import successful' },
  'import.fail_preview':    { th: 'ตรวจสอบไฟล์ไม่สำเร็จ', en: 'Failed to preview file' },
  'import.fail_import':     { th: 'นำเข้าไม่สำเร็จ', en: 'Import failed' },

  // ─── Duplicate question ───
  'q.duplicate':            { th: 'ทำซ้ำ', en: 'Duplicate' },
  'q.aria.duplicate':       { th: 'ทำซ้ำข้อสอบ', en: 'Duplicate question' },
  'q.duplicate_copying':    { th: 'กำลังคัดลอก...', en: 'Copying...' },
  'q.duplicate_ready':      { th: 'พร้อมแก้ไข — บันทึกเป็นข้อสอบใหม่', en: 'Ready to edit — save as a new question' },
  'q.duplicate_fail':       { th: 'คัดลอกไม่สำเร็จ', en: 'Duplicate failed' },

  // ─── Student view toggle ───
  'detail.student_view':    { th: 'ดูแบบนักเรียน', en: 'Student view' },
  'detail.teacher_view':    { th: 'ดูพร้อมเฉลย', en: 'Show answers' },

  // ─── Question Detail modal ───
  'detail.title':           { th: 'รายละเอียดข้อสอบ', en: 'Question detail' },
  'detail.question_label':  { th: 'คำถาม', en: 'Question' },
  'detail.options_label':   { th: 'ตัวเลือก', en: 'Options' },
  'detail.answer_label':    { th: 'คำตอบ', en: 'Answer' },
  'detail.indicators_label':{ th: 'ตัวชี้วัด', en: 'Indicators' },
  'detail.creator_label':   { th: 'ผู้สร้าง', en: 'Creator' },
  'detail.created_label':   { th: 'สร้างเมื่อ', en: 'Created' },
  'detail.points_label':    { th: 'คะแนน', en: 'Points' },
  'detail.no_question':     { th: '— ไม่มีคำถาม —', en: '— No question text —' },
  'detail.no_options':      { th: 'ไม่มีตัวเลือก', en: 'No options' },
  'detail.not_found':       { th: 'ไม่พบข้อสอบ', en: 'Question not found' },
  'detail.load_fail':       { th: 'โหลดข้อมูลไม่สำเร็จ', en: 'Failed to load' },
  'detail.explanation':     { th: 'เฉลย', en: 'Explanation' },
  'detail.history_btn':     { th: 'ประวัติการแก้ไข', en: 'Revision history' },
  'detail.download_pdf':    { th: 'ดาวน์โหลด PDF', en: 'Download PDF' },

  // ─── Revision History modal ───
  'revisions.title':        { th: 'ประวัติการแก้ไข', en: 'Revision history' },
  'revisions.desc':         { th: 'ทุกครั้งที่ข้อสอบถูกแก้ไข ระบบจะเก็บเวอร์ชันก่อนหน้าไว้', en: 'Every edit is stored as a previous version' },
  'revisions.empty':        { th: 'ยังไม่มีประวัติการแก้ไข', en: 'No revision history' },
  'revisions.empty_desc':   { th: 'เมื่อมีการแก้ไขข้อสอบ เวอร์ชันก่อนหน้าจะเก็บไว้ที่นี่', en: 'Previous versions will appear here after edits' },
  'revisions.version':      { th: 'เวอร์ชัน', en: 'Version' },
  'revisions.restore':      { th: 'กู้คืน', en: 'Restore' },
  'revisions.restore_title':{ th: 'กู้คืนเป็นเวอร์ชัน', en: 'Restore to version' },
  'revisions.restore_desc': { th: 'ข้อมูลปัจจุบันจะถูกแทนที่ด้วยเวอร์ชันนี้ (ระบบยังเก็บประวัติไว้)', en: 'Current data will be replaced by this version (history is kept)' },
  'revisions.success':      { th: 'กู้คืนเวอร์ชันเก่าเรียบร้อย', en: 'Version restored' },
  'revisions.fail':         { th: 'กู้คืนไม่สำเร็จ', en: 'Restore failed' },

  // ─── Change Password modal ───
  'password.title':         { th: 'เปลี่ยนรหัสผ่าน', en: 'Change password' },
  'password.desc':          { th: 'กรุณายืนยันรหัสผ่านปัจจุบันก่อนตั้งรหัสใหม่', en: 'Confirm your current password before setting a new one' },
  'password.current':       { th: 'รหัสผ่านปัจจุบัน', en: 'Current password' },
  'password.new':           { th: 'รหัสผ่านใหม่', en: 'New password' },
  'password.confirm':       { th: 'ยืนยันรหัสผ่านใหม่', en: 'Confirm new password' },
  'password.hint':          { th: 'อย่างน้อย 8 ตัวอักษร', en: 'At least 8 characters' },
  'password.save':          { th: 'บันทึกรหัสใหม่', en: 'Save new password' },
  'password.show':          { th: 'แสดงรหัสผ่าน', en: 'Show password' },
  'password.hide':          { th: 'ซ่อนรหัสผ่าน', en: 'Hide password' },
  'password.val.short':     { th: 'รหัสผ่านใหม่ต้องยาวอย่างน้อย 8 ตัวอักษร', en: 'New password must be at least 8 characters' },
  'password.val.mismatch':  { th: 'ยืนยันรหัสผ่านไม่ตรงกัน', en: 'Passwords do not match' },
  'password.success':       { th: 'เปลี่ยนรหัสผ่านเรียบร้อย', en: 'Password changed' },
  'password.fail':          { th: 'เปลี่ยนรหัสผ่านไม่สำเร็จ', en: 'Failed to change password' },

  // ─── Symbol picker ───
  'symbol.more':            { th: 'เพิ่มเติม', en: 'More' },
  'symbol.insert':          { th: 'แทรก', en: 'Insert' },
  'symbol.hint':            { th: 'คลิกที่ช่องคำถาม/ตัวเลือก แล้วกดสัญลักษณ์เพื่อแทรก ณ ตำแหน่งเคอร์เซอร์', en: 'Click a question/choice field, then press a symbol to insert at cursor' },
  'symbol.group.basic':     { th: 'พื้นฐาน', en: 'Basic' },
  'symbol.group.fractions': { th: 'เศษส่วน', en: 'Fractions' },
  'symbol.group.powers':    { th: 'ยกกำลัง / ตัวห้อย', en: 'Powers / Subscripts' },
  'symbol.group.roots':     { th: 'ราก / พิเศษ', en: 'Roots / Special' },
  'symbol.group.shapes':    { th: 'รูปทรง / ช่องว่าง', en: 'Shapes / Blanks' },
  'symbol.group.arrows':    { th: 'ลูกศร / มุม', en: 'Arrows / Angles' },
  'symbol.group.units':     { th: 'หน่วย / สกุลเงิน', en: 'Units / Currency' },

  // ─── Question types (shared across pages) ───
  'q.type.single_choice':   { th: 'เลือกตอบ', en: 'Single choice' },
  'q.type.multiple_answer': { th: 'เลือกหลายตอบ', en: 'Multiple answer' },
  'q.type.true_false':      { th: 'ถูก/ผิด', en: 'True / False' },
  'q.type.matching':        { th: 'จับคู่', en: 'Matching' },
  'q.type.essay':           { th: 'อัตนัย', en: 'Essay' },
  'q.type.short_answer':    { th: 'คำตอบสั้น', en: 'Short answer' },

  // ─── Detail modal extra ───
  'detail.type.single_choice':   { th: 'เลือกตอบ (1 คำตอบ)', en: 'Single choice (1 answer)' },
  'detail.type.multiple_answer': { th: 'เลือกตอบ (หลายคำตอบ)', en: 'Multiple choice (multiple answers)' },
  'detail.type.true_false':      { th: 'ถูก / ผิด', en: 'True / False' },
  'detail.type.matching':        { th: 'จับคู่', en: 'Matching' },
  'detail.type.essay':           { th: 'อัตนัย', en: 'Essay' },
  'detail.type.short_answer':    { th: 'คำตอบสั้น', en: 'Short answer' },
  'detail.image_alt':            { th: 'ภาพประกอบข้อสอบ', en: 'Question illustration' },
  'detail.correct_default':      { th: 'ถูก', en: 'True' },
  'detail.no_answer':            { th: 'ไม่พบคำตอบ', en: 'No answer found' },

  // ─── Import modal extras ───
  'import.read_failed':          { th: 'อ่านไฟล์ไม่สำเร็จ', en: 'Failed to read file' },
  'import.tpl.4_choices':        { th: 'ปรนัย 4 ตัวเลือก', en: '4-choice single answer' },
  'import.tpl.4_choices_desc':   { th: 'A/B/C/D — เลือกตอบเดียว', en: 'A/B/C/D — single answer' },
  'import.tpl.4_choices_win':    { th: 'ปรนัย 4 ตัวเลือก (Windows)', en: '4-choice single answer (Windows)' },
  'import.tpl.4_choices_win_desc': { th: 'สำหรับเปิดด้วย Excel Windows', en: 'Open with Excel on Windows' },
  'import.tpl.3_choices':        { th: 'ปรนัย 3 ตัวเลือก', en: '3-choice single answer' },
  'import.tpl.3_choices_desc':   { th: 'A/B/C — เลือกตอบเดียว', en: 'A/B/C — single answer' },
  'import.tpl.3_choices_win':    { th: 'ปรนัย 3 ตัวเลือก (Windows)', en: '3-choice single answer (Windows)' },
  'import.tpl.true_false':       { th: 'ถูก/ผิด', en: 'True / False' },
  'import.tpl.true_false_desc':  { th: 'ข้อสอบจริง-เท็จ', en: 'True/False question' },
  'import.tpl.matching':         { th: 'จับคู่', en: 'Matching' },
  'import.tpl.matching_desc':    { th: '3 คู่ ซ้าย-ขวา', en: '3 left-right pairs' },
  'import.tpl.multi_answer':     { th: 'หลายคำตอบ', en: 'Multiple answer' },
  'import.tpl.multi_answer_desc':{ th: 'ตอบได้หลายข้อ — comma-separated', en: 'Multiple answers — comma-separated' },
  'import.tpl.math':             { th: 'คณิตศาสตร์', en: 'Mathematics' },
  'import.tpl.math_desc':        { th: 'พร้อม learning indicators', en: 'With learning indicators' },
  'import.tpl.math_official':    { th: 'คณิตศาสตร์ (ทางการ)', en: 'Mathematics (official)' },
  'import.tpl.math_official_desc': { th: 'ตามหลักสูตรแกนกลาง', en: 'National core curriculum' },
  'import.tpl.comprehensive':    { th: 'แบบครบทุกฟิลด์', en: 'Comprehensive (all fields)' },
  'import.tpl.comprehensive_desc': { th: 'รวมทุกประเภท + รายงานคะแนน', en: 'All types + score report' },
  'import.group.multiple_choice':{ th: '🅰️ ปรนัย — เลือกตอบเดียว', en: '🅰️ Single-choice' },
  'import.group.true_false':     { th: '✓✗ ถูก/ผิด', en: '✓✗ True / False' },
  'import.group.matching':       { th: '🔗 จับคู่', en: '🔗 Matching' },
  'import.group.multiple_answer':{ th: '☑️ หลายคำตอบ', en: '☑️ Multiple answer' },
  'import.group.subject':        { th: '📚 ตามวิชา (พร้อมตัวชี้วัด)', en: '📚 By subject (with indicators)' },
  'import.group.comprehensive':  { th: '📋 รวมทุกประเภท', en: '📋 Comprehensive' },

  // ─── Login extra ───
  'login.brand_short':      { th: 'ธนาคารข้อสอบ', en: 'Question bank' },
  'login.footer':           { th: 'ระบบธนาคารข้อสอบ v8 · โรงเรียนใบบุญลำพูน', en: 'Question Bank v8 · Baiboon Lamphun School' },

  // ─── Dashboard extras ───
  'dash.approve_questions': { th: 'อนุมัติข้อสอบ', en: 'Approve questions' },
  'dash.no_question_text':  { th: 'ข้อสอบไม่มีคำถาม', en: 'No question text' },

  // ─── Dashboard onboarding empty state ───
  'onboard.heading':        { th: 'ยินดีต้อนรับสู่ระบบ! เริ่มต้นด้วย 3 ขั้นง่าย ๆ', en: 'Welcome! Get started in 3 easy steps' },
  'onboard.create_title':   { th: 'สร้างข้อสอบแรกของคุณ', en: 'Create your first question' },
  'onboard.create_desc':    { th: 'ใช้ wizard 5 ขั้นง่าย ๆ สร้างและบันทึกข้อสอบได้ทันที', en: 'Use the 5-step wizard to create and save your first question' },
  'onboard.import_title':   { th: 'นำเข้าข้อสอบจาก Excel/CSV', en: 'Import from Excel / CSV' },
  'onboard.import_desc':    { th: 'มีข้อสอบเดิมอยู่แล้ว? นำเข้าพร้อมกันทีเดียวได้เลย', en: 'Already have questions? Bulk-import from a spreadsheet' },
  'onboard.bank_title':     { th: 'ดูคลังข้อสอบ', en: 'Browse the question bank' },
  'onboard.bank_desc':      { th: 'ดูข้อสอบที่ผ่านการอนุมัติแล้วและนำมาสร้างชุดข้อสอบ', en: 'Browse approved questions and build exam sets' },
  'onboard.go':             { th: 'เริ่มเลย', en: 'Get started' },

  // ─── Command Palette ───
  'palette.placeholder':    { th: 'ค้นหาเมนู, คำสั่ง...', en: 'Search menu, commands...' },
  'palette.group.goto':     { th: 'ไปที่', en: 'Go to' },
  'palette.group.actions':  { th: 'คำสั่ง', en: 'Actions' },
  'palette.nav.dashboard':  { th: 'หน้าหลัก', en: 'Dashboard' },
  'palette.nav.questions':  { th: 'ข้อสอบของฉัน', en: 'My questions' },
  'palette.nav.approvals':  { th: 'อนุมัติข้อสอบ', en: 'Approvals' },
  'palette.nav.bank':       { th: 'ธนาคารข้อสอบ', en: 'Question bank' },
  'palette.nav.indicators': { th: 'จัดการตัวชี้วัด', en: 'Learning indicators' },
  'palette.nav.reports':    { th: 'รายงาน', en: 'Reports' },
  'palette.nav.users':      { th: 'จัดการผู้ใช้', en: 'Users' },
  'palette.nav.perms':      { th: 'จัดการสิทธิ์กลุ่มสาระ', en: 'Subject permissions' },
  'palette.nav.metadata':   { th: 'ข้อมูลพื้นฐาน', en: 'Metadata' },
  'palette.nav.backup':     { th: 'จัดการ Backup', en: 'Backups' },
  'palette.act.create':     { th: 'สร้างข้อสอบใหม่', en: 'Create new question' },
  'palette.empty':          { th: 'ไม่พบรายการ', en: 'No results found' },
  'palette.hint.nav':       { th: 'เลื่อน', en: 'navigate' },
  'palette.hint.select':    { th: 'เลือก', en: 'select' },

  // ─── Keyboard shortcuts help ───
  'shortcuts.title':        { th: 'คีย์ลัดทั้งหมด', en: 'All keyboard shortcuts' },
  'shortcuts.group.general':{ th: 'ทั่วไป', en: 'General' },
  'shortcuts.group.nav':    { th: 'นำทาง', en: 'Navigation' },
  'shortcuts.group.actions':{ th: 'การกระทำ', en: 'Actions' },
  'shortcuts.open_palette': { th: 'เปิด Command Palette', en: 'Open Command Palette' },
  'shortcuts.show_shortcuts':{ th: 'แสดงคีย์ลัด', en: 'Show shortcuts' },
  'shortcuts.close_modal':  { th: 'ปิด modal / dialog', en: 'Close modal / dialog' },
  'shortcuts.go_dashboard': { th: 'ไปหน้าหลัก (Dashboard)', en: 'Go to Dashboard' },
  'shortcuts.go_questions': { th: 'ไปข้อสอบของฉัน', en: 'Go to my questions' },
  'shortcuts.go_bank':      { th: 'ไปธนาคารข้อสอบ', en: 'Go to question bank' },
  'shortcuts.go_approvals': { th: 'ไปอนุมัติข้อสอบ', en: 'Go to approvals' },
  'shortcuts.create_question':{ th: 'สร้างข้อสอบใหม่', en: 'Create new question' },
  'shortcuts.focus_search': { th: 'โฟกัสช่องค้นหา', en: 'Focus search' },

  // ─── Notifications drawer ───
  'notif.title':            { th: 'แจ้งเตือน', en: 'Notifications' },
  'notif.mark_all_read':    { th: 'อ่านทั้งหมด', en: 'Mark all as read' },
  'notif.empty_title':      { th: 'ยังไม่มีแจ้งเตือน', en: 'No notifications yet' },
  'notif.empty_desc':       { th: 'คุณจะเห็นความเคลื่อนไหวเกี่ยวกับข้อสอบของคุณที่นี่', en: 'Activity related to your questions will appear here' },
  'notif.unread_aria':      { th: 'ยังไม่อ่าน', en: 'Unread' },

  // ─── Pagination ───
  'pagination.showing':     { th: 'แสดง', en: 'Showing' },
  'pagination.of':          { th: 'จาก', en: 'of' },
  'pagination.items':       { th: 'รายการ', en: 'items' },
  'pagination.prev_aria':   { th: 'หน้าก่อน', en: 'Previous page' },
  'pagination.next_aria':   { th: 'หน้าถัดไป', en: 'Next page' },

  // ─── Status badge labels (for StatusBadge component) ───
  'badge.approved':         { th: 'อนุมัติแล้ว', en: 'Approved' },
  'badge.pending':          { th: 'รออนุมัติ', en: 'Pending' },
  'badge.draft':            { th: 'ร่าง', en: 'Draft' },
  'badge.rejected':         { th: 'ไม่อนุมัติ', en: 'Rejected' },

  // ─── ConfirmDialog ───
  'confirm.irreversible':   { th: 'การกระทำนี้ไม่สามารถย้อนกลับได้ในบางกรณี — กรุณายืนยัน', en: 'This action may be irreversible — please confirm.' },

  // ─── Modal / Drawer close aria ───
  'aria.close':             { th: 'ปิด', en: 'Close' },

  // ─── Toast undo ───
  'toast.undo':             { th: 'ยกเลิก', en: 'Undo' },

  // ─── Wizard placeholder strings ───
  'wizard.topic_ph':        { th: 'เช่น บทที่ 3 — การบวกเลขสองหลัก', en: 'e.g. Chapter 3 — Two-digit addition' },
  'wizard.explanation_ph':  { th: 'อธิบายเหตุผลของคำตอบ...', en: 'Explain the reasoning for the answer...' },
  'wizard.left_ph':         { th: 'ข้อความ', en: 'Text' },
  'wizard.right_ph':        { th: 'คู่ที่ตรงกัน', en: 'Matching pair' },
  'wizard.essay_answer_ph': { th: 'เขียนตัวอย่างคำตอบที่คาดหวัง...', en: 'Write the expected model answer...' },
  'wizard.essay_criteria_ph':{ th: 'เช่น คำตอบครบถ้วน 5 คะแนน, มีตัวอย่างประกอบ 3 คะแนน...', en: 'e.g. Full answer 5 pts, includes example 3 pts...' },
  'wizard.choice_ph':       { th: 'ตัวเลือก', en: 'Choice' },
  'wizard.true_label':      { th: '✓ ถูก', en: '✓ True' },
  'wizard.false_label':     { th: '✗ ผิด', en: '✗ False' },
  'wizard.server_no_path':  { th: 'เซิร์ฟเวอร์ไม่ส่งเส้นทางรูปกลับมา', en: 'Server did not return an image path' },
  'wizard.clone_toast':     { th: 'เริ่มจากการทำซ้ำ — แก้ไขได้ตามต้องการ', en: 'Duplicated — edit as needed before saving' },

  // ─── Button extras ───
  'btn.clear':               { th: 'ล้าง', en: 'Clear' },
  'btn.download_pdf':        { th: 'ดาวน์โหลด PDF', en: 'Download PDF' },

  // ─── Common extra ───
  'common.add_new':         { th: 'เพิ่มใหม่', en: 'Add new' },
  'common.select_all':      { th: 'เลือกทั้งหมด', en: 'Select all' },
  'common.clear':           { th: 'ล้าง', en: 'Clear' },
  'common.loading':         { th: 'กำลังโหลด...', en: 'Loading...' },
  'common.no_option':       { th: '— ไม่ระบุ —', en: '— None —' },
  'common.total':           { th: 'ทั้งหมด', en: 'Total' },
  'common.save_success':    { th: 'บันทึกเรียบร้อย', en: 'Saved' },
  'common.delete_success':  { th: 'ลบเรียบร้อย', en: 'Deleted' },
  'common.irreversible':    { th: 'การลบนี้ไม่สามารถย้อนกลับได้', en: 'This action cannot be undone' },

  // ─── Question Bank extended filters ───
  'qbank.filter.all_types':        { th: 'ทุกประเภท', en: 'All types' },
  'qbank.filter.topic_ph':         { th: 'ค้นหาหัวข้อ...', en: 'Search topic...' },
  'qbank.filter.all_creators':     { th: 'ทุกคน', en: 'All creators' },
  'qbank.filter.all_indicators':   { th: 'ทุกตัวชี้วัด', en: 'All indicators' },
  'qbank.select_page':             { th: 'เลือกทั้งหน้า', en: 'Select page' },
  'qbank.deselect_page':           { th: 'ยกเลิกทั้งหน้า', en: 'Deselect page' },
  'qbank.usage_badge':             { th: 'ใช้ใน {n} ชุด', en: 'Used in {n} sets' },
  'qbank.tab.bank':                { th: 'คลังข้อสอบ', en: 'Question bank' },
  'qbank.tab.sets':                { th: 'ชุดข้อสอบของฉัน', en: 'My exam sets' },
  'qbank.view_cart':               { th: 'ดูตะกร้า', en: 'View cart' },
  'qbank.cart_title':              { th: 'ตะกร้า ({n} ข้อ)', en: 'Cart ({n} questions)' },
  'qbank.cart_empty':              { th: 'ตะกร้าว่างเปล่า', en: 'Cart is empty' },
  'qbank.cart_empty_desc':         { th: 'กดปุ่ม "เพิ่ม" ในตารางข้อสอบเพื่อเพิ่มลงตะกร้า', en: 'Click "Add" in the question table to add to the cart' },
  'qbank.cart_total_pts':          { th: 'คะแนนรวม', en: 'Total points' },
  'qbank.cart_by_difficulty':      { th: 'ตามความยาก', en: 'By difficulty' },
  'qbank.cart_by_type':            { th: 'ตามประเภท', en: 'By type' },
  'qbank.create_paper':            { th: 'สร้างกระดาษข้อสอบ', en: 'Create exam paper' },

  // ─── Exam Builder ───
  'exam.builder_title':            { th: 'สร้างกระดาษข้อสอบ', en: 'Create exam paper' },
  'exam.builder_desc':             { th: 'กรอกรายละเอียดแล้วกด "สร้าง" เพื่อเปิด PDF ในแท็บใหม่', en: 'Fill in the details then click "Create" to open the PDF in a new tab' },
  'exam.field.title':              { th: 'ชื่อข้อสอบ', en: 'Exam title' },
  'exam.field.title_ph':           { th: 'เช่น แบบทดสอบกลางภาค...', en: 'e.g. Midterm examination...' },
  'exam.field.instructions':       { th: 'คำชี้แจง', en: 'Instructions' },
  'exam.field.instructions_ph':    { th: 'เช่น ให้นักเรียนเลือกคำตอบที่ถูกต้องที่สุด...', en: 'e.g. Choose the best answer...' },
  'exam.field.school_name':        { th: 'ชื่อโรงเรียน', en: 'School name' },
  'exam.field.subject_name':       { th: 'วิชา', en: 'Subject' },
  'exam.field.grade_level':        { th: 'ระดับชั้น', en: 'Grade level' },
  'exam.field.teacher_name':       { th: 'ครูผู้ออกข้อสอบ', en: 'Teacher name' },
  'exam.field.exam_date':          { th: 'วันที่สอบ', en: 'Exam date' },
  'exam.field.time_limit':         { th: 'เวลาในการทำ', en: 'Time limit' },
  'exam.field.time_limit_ph':      { th: 'เช่น 60 นาที', en: 'e.g. 60 minutes' },
  'exam.field.total_points':       { th: 'คะแนนเต็ม', en: 'Total points' },
  'exam.field.variant_label':      { th: 'รหัสชุด (ฉบับ)', en: 'Variant label' },
  'exam.field.variant_ph':         { th: 'เช่น A, B, ก, ข', en: 'e.g. A, B' },
  'exam.switch.answer_key':        { th: 'พิมพ์เฉลยต่อท้าย', en: 'Include answer key' },
  'exam.switch.shuffle':           { th: 'สับเปลี่ยนลำดับตัวเลือก', en: 'Shuffle options' },
  'exam.btn.create':               { th: 'สร้างกระดาษข้อสอบ', en: 'Create exam paper' },
  'exam.btn.save_set':             { th: 'บันทึกเป็นชุดข้อสอบ', en: 'Save as exam set' },
  'exam.btn.multi_version':        { th: 'สร้างหลายชุด (A, B, C)', en: 'Multi-version (A, B, C)' },
  'exam.multi_title':              { th: 'สร้างหลายชุด', en: 'Multi-version export' },
  'exam.multi_desc':               { th: 'แต่ละชุดจะสลับลำดับตัวเลือกอัตโนมัติ — ทำให้ลอกกันยาก', en: 'Each version shuffles option order automatically — harder to copy' },
  'exam.multi_count_label':        { th: 'จำนวนชุด (2–5)', en: 'Number of versions (2–5)' },
  'exam.btn.generate_multi':       { th: 'สร้าง', en: 'Generate' },
  'exam.auto_points':              { th: 'คำนวณจากข้อสอบในตะกร้า', en: 'Computed from cart questions' },
  'exam.col_label':                { th: 'จำนวนคอลัมน์', en: 'Columns' },
  'exam.col_1':                    { th: '1 คอลัมน์', en: '1 column' },
  'exam.col_2':                    { th: '2 คอลัมน์', en: '2 columns' },
  'exam.btn_word':                 { th: 'ดาวน์โหลด Word (.doc)', en: 'Download Word (.doc)' },
  'exam.btn_pdf':                  { th: 'เปิดเพื่อพิมพ์ PDF', en: 'Open for PDF print' },

  // ─── Exam Sets ───
  'set.title':                     { th: 'ชุดข้อสอบของฉัน', en: 'My exam sets' },
  'set.empty':                     { th: 'ยังไม่มีชุดข้อสอบ', en: 'No exam sets yet' },
  'set.empty_desc':                { th: 'บันทึกตะกร้าเป็นชุดข้อสอบเพื่อนำกลับมาใช้ซ้ำได้', en: 'Save a cart as an exam set to reuse it later' },
  'set.q_count':                   { th: '{n} ข้อ', en: '{n} questions' },
  'set.btn.load_cart':             { th: 'เปิดในตะกร้า', en: 'Load into cart' },
  'set.btn.create_paper':          { th: 'สร้างกระดาษข้อสอบ', en: 'Create exam paper' },
  'set.btn.delete':                { th: 'ลบ', en: 'Delete' },
  'set.delete_confirm':            { th: 'ลบชุดข้อสอบนี้?', en: 'Delete this exam set?' },
  'set.save_title':                { th: 'บันทึกเป็นชุดข้อสอบ', en: 'Save as exam set' },
  'set.save_desc':                 { th: 'บันทึกข้อสอบในตะกร้าเพื่อนำกลับมาใช้ซ้ำได้', en: 'Save cart questions to reuse later' },
  'set.field.name':                { th: 'ชื่อชุดข้อสอบ *', en: 'Set name *' },
  'set.field.name_ph':             { th: 'เช่น แบบทดสอบกลางภาค ป.3', en: 'e.g. Midterm P.3' },
  'set.field.description':         { th: 'คำอธิบาย (ไม่บังคับ)', en: 'Description (optional)' },
  'set.btn.save':                  { th: 'บันทึก', en: 'Save' },
  'set.success_save':              { th: 'บันทึกชุดข้อสอบเรียบร้อย', en: 'Exam set saved' },
  'set.fail_save':                 { th: 'บันทึกไม่สำเร็จ', en: 'Failed to save' },
  'set.success_delete':            { th: 'ลบชุดข้อสอบเรียบร้อย', en: 'Exam set deleted' },
  'set.loaded_cart':               { th: 'โหลดข้อสอบลงตะกร้าแล้ว', en: 'Questions loaded into cart' },

  // ─── Question Bank table view ───
  'qbank.table.col_select':        { th: 'เลือก', en: 'Select' },
  'qbank.table.col_image':         { th: 'รูป', en: 'Image' },
  'qbank.table.col_no':            { th: 'ข้อ', en: 'No.' },
  'qbank.table.col_question':      { th: 'คำถาม', en: 'Question' },
  'qbank.table.col_subject':       { th: 'วิชา', en: 'Subject' },
  'qbank.table.col_grade':         { th: 'ชั้น', en: 'Grade' },
  'qbank.table.col_type':          { th: 'ประเภท', en: 'Type' },
  'qbank.table.col_difficulty':    { th: 'ความยาก', en: 'Difficulty' },
  'qbank.table.col_creator':       { th: 'ผู้สร้าง', en: 'Creator' },
  'qbank.table.col_usage':         { th: 'ใช้แล้ว', en: 'Used' },
  'qbank.table.col_actions':       { th: 'การดำเนินการ', en: 'Actions' },
  'qbank.table.usage_sets':        { th: '{n} ชุด', en: '{n} sets' },
  'qbank.table.no_image':          { th: '—', en: '—' },

  // ─── Question Bank bulk actions ───
  'qbank.bulk.selected':           { th: 'เลือกแล้ว {n} ข้อ', en: '{n} selected' },
  'qbank.bulk.select_page':        { th: 'เลือกทั้งหน้า', en: 'Select page' },
  'qbank.bulk.deselect_page':      { th: 'ยกเลิกทั้งหน้า', en: 'Deselect page' },
  'qbank.bulk.open_cart':          { th: 'เปิดตะกร้า', en: 'Open cart' },
  'qbank.bulk.delete_selected':    { th: 'ลบที่เลือก', en: 'Delete selected' },

  // ─── Delete (admin) ───
  'delete.title_single':           { th: 'ยืนยันการลบข้อสอบ', en: 'Confirm question deletion' },
  'delete.title_bulk':             { th: 'ยืนยันการลบข้อสอบ {n} ข้อ', en: 'Confirm deletion of {n} questions' },
  'delete.desc':                   { th: 'ข้อสอบต่อไปนี้จะถูกลบออกจากระบบถาวร ไม่สามารถกู้คืนได้', en: 'The following questions will be permanently removed and cannot be recovered.' },
  'delete.success':                { th: 'ลบสำเร็จ {n} ข้อ', en: 'Deleted {n} questions' },
  'delete.fail':                   { th: 'ลบไม่สำเร็จ', en: 'Delete failed' },
  'delete.btn_confirm':            { th: 'ลบถาวร', en: 'Delete permanently' },

  // ─── Password confirm modal ───
  'pwd.placeholder':               { th: 'กรอกรหัสผ่านของคุณเพื่อยืนยัน', en: 'Enter your password to confirm' },
  'pwd.label':                     { th: 'รหัสผ่านของคุณ', en: 'Your password' },
  'pwd.wrong':                     { th: 'รหัสผ่านไม่ถูกต้อง', en: 'Incorrect password' },

  // ─── Admin password reset (pwd.reset.*) ───────────────────────────────────
  'pwd.reset.title':               { th: 'รีเซ็ตรหัสผ่าน', en: 'Reset password' },
  'pwd.reset.desc':                { th: 'ตั้งรหัสผ่านใหม่ให้ผู้ใช้งาน — จำเป็นต้องยืนยันด้วยรหัสผ่านของคุณ', en: 'Set a new password for this user — your own password is required to confirm.' },
  'pwd.reset.btn':                 { th: 'รีเซ็ตรหัสผ่าน', en: 'Reset password' },
  'pwd.reset.btn_aria':            { th: 'รีเซ็ตรหัสผ่านผู้ใช้', en: 'Reset user password' },
  'pwd.reset.new_password':        { th: 'รหัสผ่านใหม่ (อย่างน้อย 8 ตัวอักษร)', en: 'New password (at least 8 characters)' },
  'pwd.reset.confirm_password':    { th: 'ยืนยันรหัสผ่านใหม่', en: 'Confirm new password' },
  'pwd.reset.admin_password':      { th: 'รหัสผ่านของคุณ (เพื่อยืนยันสิทธิ์)', en: 'Your password (to confirm authority)' },
  'pwd.reset.admin_password_hint': { th: 'กรอกรหัสผ่านของผู้ดูแลระบบเพื่อยืนยันการกระทำ', en: 'Enter your admin password to authorise this action' },
  'pwd.reset.hint':                { th: 'อย่างน้อย 8 ตัวอักษร', en: 'At least 8 characters' },
  'pwd.reset.success':             { th: 'รีเซ็ตรหัสผ่านเรียบร้อยแล้ว', en: 'Password reset successfully' },
  'pwd.reset.fail':                { th: 'รีเซ็ตรหัสผ่านไม่สำเร็จ', en: 'Password reset failed' },
  'pwd.reset.val.short':           { th: 'รหัสผ่านใหม่ต้องมีอย่างน้อย 8 ตัวอักษร', en: 'New password must be at least 8 characters' },
  'pwd.reset.val.mismatch':        { th: 'รหัสผ่านใหม่และรหัสผ่านยืนยันไม่ตรงกัน', en: 'New password and confirmation do not match' },
  'pwd.reset.val.admin_required':  { th: 'กรุณากรอกรหัสผ่านของคุณเพื่อยืนยัน', en: 'Please enter your password to confirm' },

  // ─── Curriculum coverage report (coverage.*) ─────────────────────────────
  'reports.tab.coverage':          { th: 'ความครอบคลุมหลักสูตร', en: 'Curriculum coverage' },
  'coverage.summary.total':        { th: 'ตัวชี้วัดทั้งหมด', en: 'Total indicators' },
  'coverage.summary.covered':      { th: 'มีข้อสอบแล้ว', en: 'Covered' },
  'coverage.summary.uncovered':    { th: 'ยังขาดข้อสอบ', en: 'Not covered' },
  'coverage.summary.questions':    { th: 'ข้อสอบที่อนุมัติแล้ว', en: 'Approved questions' },
  'coverage.filter.subject':       { th: 'กลุ่มสาระวิชา', en: 'Subject group' },
  'coverage.filter.grade':         { th: 'ระดับชั้น', en: 'Grade level' },
  'coverage.col.code':             { th: 'รหัสตัวชี้วัด', en: 'Indicator code' },
  'coverage.col.title':            { th: 'ชื่อตัวชี้วัด', en: 'Indicator title' },
  'coverage.col.count':            { th: 'จำนวนข้อ', en: 'Questions' },
  'coverage.total_label':          { th: 'รวม', en: 'Total' },
  'coverage.empty_hint':           { th: 'เลือกวิชาหรือระดับชั้นเพื่อดูความครอบคลุมหลักสูตร', en: 'Select a subject or grade to view curriculum coverage' },
  'coverage.no_indicators':        { th: 'ไม่พบตัวชี้วัดในเกณฑ์ที่เลือก', en: 'No indicators found for the selected filters' },
  'coverage.row_hint':             { th: 'คลิกเพื่อดูข้อสอบของตัวชี้วัดนี้', en: 'Click to view questions for this indicator' },

  // ─── Cognitive Level Report ──────────────────────────────────────────────────
  'reports.tab.by_cognitive':      { th: 'ตามระดับพฤติกรรม', en: 'By Cognitive Level' },
  'reports.cognitive.title':       { th: 'ข้อสอบตามระดับพฤติกรรม (Bloom)', en: 'Questions by Cognitive Level (Bloom)' },
  'reports.cognitive.matrix_title':{ th: 'เมทริกซ์ระดับพฤติกรรม × กลุ่มสาระ', en: 'Cognitive Level × Subject Matrix' },
  'reports.cognitive.col_total':   { th: 'รวม', en: 'Total' },
  'reports.cognitive.unspecified': { th: 'ไม่ระบุระดับ', en: 'Unspecified' },
  'reports.cognitive.no_data':     { th: 'ยังไม่มีข้อมูลระดับพฤติกรรม', en: 'No cognitive level data yet' },
  'reports.cognitive.cell_hint':   { th: 'คลิกเพื่อดูข้อสอบกลุ่มนี้', en: 'Click to browse these questions' },

  // ─── Difficulty Report ───────────────────────────────────────────────────────
  'reports.tab.by_difficulty':     { th: 'ตามระดับความยาก', en: 'By Difficulty' },
  'reports.difficulty.title':      { th: 'ข้อสอบตามระดับความยาก', en: 'Questions by Difficulty' },
  'reports.difficulty.breakdown':  { th: 'สัดส่วนระดับความยาก', en: 'Difficulty breakdown' },
  'reports.difficulty.no_data':    { th: 'ยังไม่มีข้อมูลระดับความยาก', en: 'No difficulty data yet' },

  // ─── Indicators × Grade Report ───────────────────────────────────────────────
  'reports.tab.indicators_by_grade': { th: 'ตัวชี้วัด × ระดับชั้น', en: 'Indicators × Grade' },
  'reports.ind_grade.title':       { th: 'ตัวชี้วัดตามระดับชั้น', en: 'Indicators by Grade Level' },
  'reports.ind_grade.filter_subject': { th: 'กรองตามกลุ่มสาระ', en: 'Filter by subject group' },
  'reports.ind_grade.col_indicator': { th: 'ตัวชี้วัด', en: 'Indicator' },
  'reports.ind_grade.col_total':   { th: 'รวม', en: 'Total' },
  'reports.ind_grade.no_data':     { th: 'ยังไม่มีข้อมูลตัวชี้วัด', en: 'No indicator data yet' },
  'reports.ind_grade.select_subject': { th: 'เลือกกลุ่มสาระเพื่อดูตัวชี้วัด', en: 'Select a subject group to view indicators' },
  'reports.ind_grade.empty_filter': { th: 'ไม่พบตัวชี้วัดในกลุ่มสาระที่เลือก', en: 'No indicators found for the selected subject' },

  // ─── Cart coverage summary ────────────────────────────────────────────────
  'qbank.cart_coverage_label':       { th: 'รายละเอียดการครอบคลุม', en: 'Coverage' },
  'qbank.cart_indicators':           { th: 'ตัวชี้วัด', en: 'indicators' },
  'qbank.cart_subjects':             { th: 'วิชา', en: 'subjects' },
  'qbank.cart_grades':               { th: 'ระดับชั้น', en: 'grades' },

  // ─── Smart Search ─────────────────────────────────────────────────────────
  'search.recent':                   { th: 'ล่าสุด', en: 'Recent' },
  'search.suggestions':              { th: 'คำแนะนำ', en: 'Suggestions' },
  'search.no_suggestions':           { th: 'ไม่มีคำแนะนำ', en: 'No suggestions' },
  'search.clear_recent':             { th: 'ล้างประวัติ', en: 'Clear history' },

  // ─── Tags ─────────────────────────────────────────────────────────────────
  'tag.field_label':                 { th: 'แท็ก (ไม่บังคับ)', en: 'Tags (optional)' },
  'tag.input_ph':                    { th: 'พิมพ์เพื่อค้นหาหรือสร้างใหม่...', en: 'Type to search or create...' },
  'tag.create_new':                  { th: 'สร้างแท็กใหม่', en: 'Create new tag' },
  'tag.hint':                        { th: 'พิมพ์ชื่อแท็กแล้วกด Enter เพื่อสร้าง · เช่น "ปลายภาค", "เก็บคะแนน"', en: 'Type tag name and press Enter to create · e.g. "Final exam"' },
  'tag.section_label':               { th: 'แท็ก', en: 'Tags' },
  'tag.filter':                      { th: 'แท็กทั้งหมด', en: 'All tags' },

  // ─── Notifications real-time ──────────────────────────────────────────────
  'notif.new_arrived':               { th: 'มีการแจ้งเตือนใหม่', en: 'New notifications' },
  'notif.new_count':                 { th: 'รายการใหม่', en: 'new' },
} as const;

export type TranslationKey = keyof typeof dict;
export const translations = dict;

export function translate(key: string, lang: Lang): string {
  const entry = (dict as Record<string, { th: string; en: string }>)[key];
  if (!entry) return key;
  return entry[lang] ?? entry.th ?? key;
}
